import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/lib/api';
import { Link } from 'react-router-dom';
import { SURVEYS } from '@/constants/testIds';
import { Plus, ClipboardText, ArrowRight, DotsThreeVertical, Trash, PencilSimple } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import ConfirmDialog from '@/components/ConfirmDialog';

export default function CheckIns() {
    const qc = useQueryClient();
    const { data: surveys = [] } = useQuery({ queryKey: ['surveys'], queryFn: async () => (await api.get('/surveys')).data });
    const [open, setOpen] = useState(false);
    const [name, setName] = useState('');
    const [desc, setDesc] = useState('');
    const [del, setDel] = useState(null);

    const create = async () => {
        if (!name.trim()) return;
        try {
            await api.post('/surveys', { name, description: desc, steps: [] });
            toast.success('Check-in created');
            setOpen(false); setName(''); setDesc('');
            qc.invalidateQueries({ queryKey: ['surveys'] });
        } catch { toast.error('Failed'); }
    };
    const doDelete = async () => {
        try {
            await api.delete(`/surveys/${del.survey_id}`);
            toast.success('Deleted');
            setDel(null);
            qc.invalidateQueries({ queryKey: ['surveys'] });
        } catch { toast.error('Failed'); }
    };

    return (
        <div data-testid={SURVEYS.root} className="px-6 md:px-10 py-8 max-w-7xl">
            <header className="mb-8 flex items-end justify-between">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Templates</div>
                    <h1 className="font-heading text-4xl md:text-5xl text-slate-900 mt-1">Check-ins</h1>
                    <p className="text-slate-600 mt-2 max-w-2xl">Short conversations you can send by link. Concerning answers show up under Needs your attention.</p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button data-testid={SURVEYS.addButton} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90">
                            <Plus size={16} className="mr-1" /> New check-in
                        </Button>
                    </DialogTrigger>
                    <DialogContent>
                        <DialogHeader><DialogTitle className="font-heading text-2xl">New check-in</DialogTitle></DialogHeader>
                        <div className="space-y-3">
                            <div><Label>Name</Label><Input data-testid={SURVEYS.nameInput} value={name} onChange={e => setName(e.target.value)} placeholder="Weekly wellness check-in" /></div>
                            <div><Label>Description</Label><Textarea data-testid={SURVEYS.descInput} value={desc} onChange={e => setDesc(e.target.value)} /></div>
                        </div>
                        <DialogFooter><Button data-testid={SURVEYS.submit} onClick={create} className="rounded-full bg-primary text-primary-foreground">Create</Button></DialogFooter>
                    </DialogContent>
                </Dialog>
            </header>

            {surveys.length === 0 ? (
                <div className="rounded-xl border border-dashed border-slate-300 py-16 text-center">
                    <ClipboardText size={40} className="mx-auto text-slate-400" />
                    <div className="font-heading text-2xl mt-3 text-slate-800">No check-ins yet</div>
                    <p className="text-slate-500 text-sm mt-1">Create one you can send whenever you&apos;d like a quick update.</p>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {surveys.map(s => (
                        <div key={s.survey_id} data-testid={SURVEYS.card} className="rounded-xl border border-slate-200 bg-card p-5 shadow-sm hover:border-primary transition-colors duration-150 group relative">
                            <div className="absolute top-3 right-3">
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><DotsThreeVertical size={18} weight="bold" /></button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem asChild><Link to={`/check-ins/${s.survey_id}`}><PencilSimple size={14} className="mr-2" /> Edit</Link></DropdownMenuItem>
                                        <DropdownMenuItem data-testid={SURVEYS.deleteButton} onClick={() => setDel(s)} className="text-red-600 focus:text-red-700"><Trash size={14} className="mr-2" /> Delete</DropdownMenuItem>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </div>
                            <Link to={`/check-ins/${s.survey_id}`} className="block">
                                <div className="font-heading text-2xl text-slate-900 pr-8">{s.name}</div>
                                {s.description && <div className="text-sm text-slate-600 mt-1 line-clamp-2">{s.description}</div>}
                                <div className="mt-5 flex items-center justify-between text-sm">
                                    <span className="text-slate-500">{s.steps?.length || 0} step{s.steps?.length === 1 ? '' : 's'}</span>
                                    <span data-testid={SURVEYS.editLink} className="inline-flex items-center gap-1 text-primary group-hover:translate-x-0.5 transition-transform duration-150">Edit <ArrowRight size={14} /></span>
                                </div>
                            </Link>
                        </div>
                    ))}
                </div>
            )}

            <ConfirmDialog
                open={!!del} onOpenChange={(v) => !v && setDel(null)}
                title="Delete this check-in?"
                description={del ? `"${del.name}" and all sent copies will be removed. This can't be undone.` : ''}
                onConfirm={doDelete}
            />
        </div>
    );
}
